/**
 * @fileoverview Notify Me Component
 * Handles the "Notify Me" form submission for back-in-stock notifications.
 * Sends subscription requests to the API and handles success/error responses.
 */

import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';
import { NotifyMeAddedEvent } from '@theme/events';

/* ================== SHARED UTILITY FUNCTIONS ================== */

/**
 * Splits a full name into first and last name
 * @param {string} fullName
 * @returns {{firstName: string, lastName: string}}
 */
export function splitName(fullName) {
  const parts = fullName.split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }

  if (parts.length === 1) {
    return { firstName: parts[0] || '', lastName: '' };
  }

  // First part is first name, rest is last name
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ');

  return { firstName, lastName };
}

/**
 * Validates email format using comprehensive regex
 * @param {string} email
 * @returns {boolean}
 */
export function isValidEmail(email) {
  // Comprehensive email regex that validates:
  // - Local part: allows letters, numbers, dots, hyphens, underscores, plus signs
  // - Domain: requires at least one dot, valid characters
  // - TLD: must be at least 2 characters
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
  return emailRegex.test(email);
}

/**
 * Gets translation from form element data attributes or global theme
 * @param {HTMLElement} formElement - Form element with data attributes
 * @param {string} key - Translation key (e.g., 'error_name_required')
 * @param {string} defaultValue - Default value if translation not found
 * @returns {string}
 */
export function getNotifyMeTranslation(formElement, key, defaultValue) {
  // Try to get translation from data attribute
  const translationKey = `notify_me_${key}`;
  const translation = formElement?.dataset?.[translationKey];

  if (translation) {
    return translation;
  }

  // Fallback to global theme translations
  const theme = /** @type {any} */ (window).theme;
  if (theme?.strings?.[`notify_me.${key}`]) {
    return theme.strings[`notify_me.${key}`];
  }

  // Fallback to default
  return defaultValue;
}

/**
 * Sets an error state on a field with message
 * Supports both notify-me and quick-view-notify-me field containers
 * @param {HTMLInputElement} field - Input field
 * @param {string} message - Error message
 */
export function setNotifyMeFieldError(field, message) {
  field.classList.add('notify-me__input--error', 'quick-view-notify-me__input--error');
  field.setAttribute('aria-invalid', 'true');

  // Try both field container class names (notify-me and quick-view-notify-me)
  const fieldContainer = field.closest('.notify-me__field') || 
                         field.closest('.quick-view-notify-me__field') ||
                         field.parentElement;

  if (fieldContainer) {
    let errorElement = fieldContainer.querySelector('.notify-me__field-error') ||
                       fieldContainer.querySelector('.quick-view-notify-me__field-error');

    if (!errorElement) {
      errorElement = document.createElement('span');
      errorElement.className = 'notify-me__field-error quick-view-notify-me__field-error';
      errorElement.setAttribute('role', 'alert');
      errorElement.id = `${field.id || 'field'}-error-${Date.now()}`;
      fieldContainer.appendChild(errorElement);

      // Associate error with input for screen readers
      field.setAttribute('aria-describedby', errorElement.id);
    }

    errorElement.textContent = message;
  }
}

/**
 * Clears the error state from a field
 * Supports both notify-me and quick-view-notify-me field containers
 * @param {HTMLInputElement} field - Input field
 */
export function clearNotifyMeFieldError(field) {
  field.classList.remove('notify-me__input--error', 'quick-view-notify-me__input--error');
  field.removeAttribute('aria-invalid');
  field.removeAttribute('aria-describedby');

  // Try both field container class names
  const fieldContainer = field.closest('.notify-me__field') || 
                         field.closest('.quick-view-notify-me__field') ||
                         field.parentElement;

  if (fieldContainer) {
    const errorElement = fieldContainer.querySelector('.notify-me__field-error') ||
                         fieldContainer.querySelector('.quick-view-notify-me__field-error');
    errorElement?.remove();
  }
}

/**
 * Validates the name field
 * @param {HTMLInputElement} field - Name input field
 * @param {HTMLElement} formElement - Form element for translations
 * @returns {boolean}
 */
export function validateNotifyMeNameField(field, formElement) {
  const value = field.value.trim();

  if (!value || value.length === 0) {
    setNotifyMeFieldError(field, getNotifyMeTranslation(formElement, 'error_name_required', 'Name is required'));
    return false;
  }

  if (value.length < 2) {
    setNotifyMeFieldError(field, getNotifyMeTranslation(formElement, 'error_name_min_length', 'Name must be at least 2 characters'));
    return false;
  }

  clearNotifyMeFieldError(field);
  return true;
}

/**
 * Validates the email field
 * @param {HTMLInputElement} field - Email input field
 * @param {HTMLElement} formElement - Form element for translations
 * @returns {boolean}
 */
export function validateNotifyMeEmailField(field, formElement) {
  const value = field.value.trim();

  if (!value || value.length === 0) {
    setNotifyMeFieldError(field, getNotifyMeTranslation(formElement, 'error_email_required', 'Email is required'));
    return false;
  }

  if (!isValidEmail(value)) {
    setNotifyMeFieldError(field, getNotifyMeTranslation(formElement, 'error_email_invalid', 'Please enter a valid email address'));
    return false;
  }

  clearNotifyMeFieldError(field);
  return true;
}

/**
 * Gets error message from API response
 * @param {HTMLElement} formElement - Form element for translations
 * @param {any} data - API response data
 * @returns {string}
 */
export function getNotifyMeErrorMessage(formElement, data) {
  if (data.code === 'INVALID_CUSTOMER_ID') {
    return getNotifyMeTranslation(formElement, 'error_invalid_customer', 'Invalid customer');
  }
  if (data.code === 'NO_SESSION_FOUND') {
    return getNotifyMeTranslation(formElement, 'error_unauthorized', 'Session expired');
  }
  if (data.code === 'INTERNAL_SERVER_ERROR') {
    return getNotifyMeTranslation(formElement, 'error_server', 'Server error');
  }
  return getNotifyMeTranslation(formElement, 'error_generic', 'An error occurred');
}

/**
 * Submits notification request to API
 * Main API function that can be used by both components
 * @param {NotifyMeFormData} formData - Form data to submit
 * @param {string} apiBaseUrl - API base URL (defaults to '/apps/notify-me/v1')
 * @param {AbortController | null} abortController - Optional abort controller
 * @returns {Promise<NotifyMeFetchResponse>}
 */
export async function submitNotifyMeNotification(formData, apiBaseUrl = '/apps/notify-me/v1', abortController = null) {
  // Create abort controller if not provided
  const controller = abortController || new AbortController();

  const url = `${apiBaseUrl}/notify`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
    signal: controller.signal,
  });

  /** @type {NotifyMeApiResponse} */
  const data = await response.json();

  return {
    ok: response.ok,
    status: response.status,
    data,
  };
}

/**
 * @typedef {Object} NotifyMeFormData
 * @property {string} sku - Product SKU
 * @property {string} email - Customer email
 * @property {string} customerFirstName - Customer first name
 * @property {string} [customerLastName] - Customer last name (optional)
 * @property {string | null} [subscriptionId] - Subscription ID (optional)
 * @property {string} [variantId] - Variant ID (optional)
 * @property {string} [customerId] - Customer ID (optional)
 */

/**
 * @typedef {Object} NotifyMeApiResponse
 * @property {boolean} success - Whether the request was successful
 * @property {string} message - Response message
 * @property {string} code - Response code
 * @property {Object} [data] - Response data
 * @property {string | Object} [error] - Error details
 */

/**
 * @typedef {Object} NotifyMeFetchResponse
 * @property {boolean} ok - Whether the fetch was successful
 * @property {number} status - HTTP status code
 * @property {NotifyMeApiResponse} data - API response data
 */

/**
 * API Error types for error handling
 * @enum {string}
 */
const ErrorCodes = {
  INVALID_CUSTOMER_ID: 'INVALID_CUSTOMER_ID',
  NOTIFICATION_EXISTS: 'NOTIFICATION_EXISTS',
  NO_SESSION_FOUND: 'NO_SESSION_FOUND',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
};

/**
 * API Response status codes
 * @enum {string}
 */
const StatusCodes = {
  SUCCESS_CREATED: '201',
  SUCCESS_EXISTS: '200',
  BAD_REQUEST: '400',
  UNAUTHORIZED: '401',
  VALIDATION_ERROR: '422',
  SERVER_ERROR: '500',
  CODE: "OK",
};

/**
 * Notify Me Form Component
 * Custom element that handles the form submission for back-in-stock notifications.
 *
 * @typedef {Object} NotifyMeFormRefs
 * @property {HTMLInputElement} skuInput - Hidden input for product SKU
 * @property {HTMLInputElement} nameInput - Input for customer name
 * @property {HTMLInputElement} emailInput - Input for customer email
 * @property {HTMLButtonElement} submitButton - Form submit button
 *
 * @extends {Component<NotifyMeFormRefs>}
 */
class NotifyMeFormComponent extends Component {
  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {AbortController | null} */
  #abortController = null;

  /**
   * Called when the element is connected to the document's DOM.
   */
  connectedCallback() {
    super.connectedCallback();
    this.submitButton = this.querySelector('.notify-me__submit');
    this.submitButton?.addEventListener('click', this.#handleSubmit.bind(this));
    this.#setupRealtimeValidation();
    this.#setupDialogCloseHandler();
    this.formData = this.#collectFormData();
    this.customerID = this.dataset.customerId;
    this.isFormSubmitted = false;
    if (this.customerID && this.formData.sku !== '') {
      this.#fetchLoggedInCustomerNotificationData();
    }
  }

  /**
   * Fetches the logged in customer notification data
   */
  async #fetchLoggedInCustomerNotificationData() {
    const url = `${this.apiBaseUrl}/list?sku=[${this.formData.sku}]`;
    const response = await fetch(url);
    /** @type {any} */
    const data = await response.json();
    if (!data.success || !data.data) {
      return;
    }
    /** @type {any} */
    const responseData = data.data;
    if (!Array.isArray(responseData.results)) {
      return;
    }
    /** @type {Array<{ sku: string }>} */
    const results = responseData.results;
    const getCurrentCustomerNotification = results.find(
      /** @param {{ sku: string }} notification */
      (notification) => notification.sku === this.formData.sku
    );
    
    if (getCurrentCustomerNotification) {
      this.isFormSubmitted = true;
      /** @type {any} */
      const eventData = getCurrentCustomerNotification;
      this.#dispatchNotifyMeAddedEvent(eventData);
      this.#showMessage(this.#getTranslation('notify_me.already_subscribed_message', "You have already subscribed for Notify Me Service! You will be notified when the item is back in stock."), 'success');
      this.#changeSuccessState();
    } else {
      // Variant is not subscribed - clear bell icons
      this.#clearBellIcons();
    }
  }

  /**
   * Clear bell icons for all variants
   */
  #clearBellIcons() {
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.querySelectorAll('.variant-option__button-label__icon.is-notified').forEach((el) => {
        el.classList.remove('is-notified');
      });
    }
  }

  /**
   * Called when the element is disconnected from the document's DOM.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('submit', this.#handleSubmit.bind(this));
    this.#abortController?.abort();
    this.#removeDialogCloseHandler();
  }

  /** @type {() => void} */
  #boundHandleDialogClose = () => {};

  /**
   * Sets up handler to reset form when dialog closes
   */
  #setupDialogCloseHandler() {
    // Create bound handler for cleanup
    this.#boundHandleDialogClose = this.#handleDialogClose.bind(this);

    // Find the parent dialog element
    const dialog = this.closest('dialog');
    if (dialog) {
      // Listen for the native close event on the dialog
      dialog.addEventListener('close', this.#boundHandleDialogClose);
    }

    // Also listen for the custom DialogCloseEvent from dialog-component
    const dialogComponent = this.closest('dialog-component');
    if (dialogComponent) {
      dialogComponent.addEventListener(DialogCloseEvent.eventName, this.#boundHandleDialogClose);
    }
  }

  /**
   * Removes dialog close handler
   */
  #removeDialogCloseHandler() {
    const dialog = this.closest('dialog');
    if (dialog) {
      dialog.removeEventListener('close', this.#boundHandleDialogClose);
    }

    const dialogComponent = this.closest('dialog-component');
    if (dialogComponent) {
      dialogComponent.removeEventListener(DialogCloseEvent.eventName, this.#boundHandleDialogClose);
    }
  }

  /**
   * Handles dialog close event - resets the form
   */
  #handleDialogClose() {
    this.#resetFormCompletely();
  }

  /**
   * Completely resets the form including all fields and error states
   */
  #resetFormCompletely() {

    if (this.customerID || this.isFormSubmitted) {
      return;
    }
    
    // Reset all input values
    const inputs = this.querySelectorAll('input:not([type="hidden"])');
    inputs.forEach((input) => {
      const inputElement = /** @type {HTMLInputElement} */ (input);
      inputElement.value = '';
    });

    // Clear all error states
    const nameInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="name"]'));
    const emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="email"]'));

    if (nameInput) {
      this.#clearFieldError(nameInput);
    }

    if (emailInput) {
      this.#clearFieldError(emailInput);
    }

    // Remove any message elements
    const existingMessage = this.querySelector('.notify-me__message');
    existingMessage?.remove();

    // Reset loading state if any
    this.#setLoadingState(false);

    // Reset success state
    this.#resetSuccessState();

    // show the form states
    this.#showFormStates();

    // Cancel any pending request
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Sets up real-time validation on form fields
   */
  #setupRealtimeValidation() {
    const nameInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="name"]'));
    const emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="email"]'));

    // Validate name on blur
    if (nameInput) {
      nameInput.addEventListener('blur', () => {
        this.#validateNameField(nameInput);
      });

      // Clear error on input
      nameInput.addEventListener('input', () => {
        this.#clearFieldError(nameInput);
      });
    }

    // Validate email on blur
    if (emailInput) {
      emailInput.addEventListener('blur', () => {
        this.#validateEmailField(emailInput);
      });

      // Clear error on input
      emailInput.addEventListener('input', () => {
        this.#clearFieldError(emailInput);
      });
    }

    const updateSubmitButtonState = () => {
      const submitButton = /** @type {HTMLButtonElement | null} */ (this.submitButton);
      if (!submitButton) return;
      const nameValue = nameInput?.value?.trim() ?? '';
      const emailValue = emailInput?.value?.trim() ?? '';
      submitButton.disabled = !(nameValue && emailValue);
    };

    [nameInput, emailInput].forEach((input) => {
      if (input) {
        input.addEventListener('input', updateSubmitButtonState);
      }
    });
    
    // Update submit button state initially
    updateSubmitButtonState();
  }

  /**
   * Validates the name field
   * @param {HTMLInputElement} field
   * @returns {boolean}
   */
  #validateNameField(field) {
    return validateNotifyMeNameField(field, this);
  }

  /**
   * Validates the email field
   * @param {HTMLInputElement} field
   * @returns {boolean}
   */
  #validateEmailField(field) {
    return validateNotifyMeEmailField(field, this);
  }

  /**
   * Sets an error state on a field with message
   * @param {HTMLInputElement} field
   * @param {string} message
   */
  #setFieldError(field, message) {
    setNotifyMeFieldError(field, message);
  }

  /**
   * Clears the error state from a field
   * @param {HTMLInputElement} field
   */
  #clearFieldError(field) {
    clearNotifyMeFieldError(field);
  }

  /**
   * Gets the API base URL from data attribute or defaults to relative path
   * @returns {string}
   */
  get apiBaseUrl() {
    return this.dataset.apiBaseUrl || '/apps/notify-me/v1';
  }

  /**
   * Handles the form submission
   * @param {Event} event
   */
  async #handleSubmit(event) {
    
    event.preventDefault();
    if (this.#isSubmitting) return;

    const formData = this.#collectFormData();

    if (!this.#validateFormData(formData)) {
      return;
    }
    this.#setLoadingState(true);
    try {
      const response = await this.#submitNotification(formData);;
      this.#handleResponse(response);
    } catch (error) {
      this.#handleError(error);
    } finally {
      this.#setLoadingState(false);
    }
  }

  /**
   * Collects form data from inputs
   * @returns {NotifyMeFormData} Form data object
   */
  #collectFormData() {
    const skuInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="sku"]'));
    const nameInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="name"]'));
    const emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="email"]'));
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="variant_id"]'));
    const fullName = nameInput?.value?.trim() || '';
    const { firstName, lastName } = this.#splitName(fullName);

    return {
      sku: skuInput?.value?.trim() || '',
      email: emailInput?.value?.trim() || '',
      customerFirstName: firstName,
      customerLastName: lastName,
      variantId: variantIdInput?.value?.trim(),
    };
  }

  /**
   * Splits a full name into first and last name
   * @param {string} fullName
   * @returns {{firstName: string, lastName: string}}
   */
  #splitName(fullName) {
    return splitName(fullName);
  }

  /**
   * Validates the form data
   * @param {NotifyMeFormData} formData
   * @returns {boolean}
   */
  #validateFormData(formData) {
    const nameInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="name"]'));
    const emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="email"]'));

    /** @type {string[]} */
    const errors = [];
    /** @type {HTMLInputElement | null} */
    let firstInvalidField = null;

    // Validate SKU (should always be present from hidden field)
    if (!formData.sku || formData.sku.length === 0) {
      errors.push('Product SKU is missing');
    }

    // Validate Name
    if (nameInput) {
      const isNameValid = this.#validateNameField(nameInput);
      if (!isNameValid) {
        errors.push(this.#getTranslation('notify_me.error_name_required', 'Name is required'));
        if (!firstInvalidField) {
          firstInvalidField = nameInput;
        }
      }
    }

    // Validate Email
    if (emailInput) {
      const isEmailValid = this.#validateEmailField(emailInput);
      if (!isEmailValid) {
        // Error message is already set by validateEmailField
        if (!firstInvalidField) {
          firstInvalidField = emailInput;
        }
      }
    }

    // Focus on first invalid field
    if (firstInvalidField) {
      firstInvalidField.focus();
      return false;
    }

    return errors.length === 0;
  }

  /**
   * Validates email format using comprehensive regex
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return isValidEmail(email);
  }

  /**
   * Submits the notification request to the API
   * @param {NotifyMeFormData} formData
   * @returns {Promise<NotifyMeFetchResponse>}
   */
  async #submitNotification(formData) {
    // Cancel any pending request
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const response = await submitNotifyMeNotification(formData, this.apiBaseUrl, this.#abortController);
    
    // Dispatch event if data exists
    if (response.data?.data) {
      /** @type {any} */
      const eventData = response.data.data;
      this.#dispatchNotifyMeAddedEvent(eventData);
    }

    return response;
  }

  /**
   * Handles the API response
   * @param {NotifyMeFetchResponse} response
   */
  #handleResponse(response) {
    const { ok, data } = response;

    if (ok && data.success) {
      this.isFormSubmitted = true;
      this.#handleSuccess(data);
    } else {
      this.#handleApiError(data);
    }
  }

  /**
   * Handles successful subscription
   * @param {NotifyMeApiResponse} data
   */
  #handleSuccess(data) {
    const isAlreadySubscribed = data.code === StatusCodes.CODE;
    
    const message = data.message;
    this.#showMessage(message, 'success');
    this.#changeSuccessState();
    // Reset form on new subscription

    if (!isAlreadySubscribed) {
      this.#resetFormCompletely();
    }
  }

  /**
   * Changes the success state of the form
   */
  #changeSuccessState() {
    const submitButton = /** @type {HTMLButtonElement | null} */ (this.submitButton);
    
    if (submitButton) {
      submitButton.classList.add('notify-me__submit--success');
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      const submitText = submitButton.querySelector(".notify-me__submit-text");
      if (submitText instanceof HTMLElement) {
        submitText.textContent = "Thank you!";
      }
    }
  }

  /**
   * Resets the success state of the form
   */
  #resetSuccessState() {
    const submitButton = /** @type {HTMLButtonElement | null} */ (this.submitButton);
    if (submitButton) {
      submitButton.classList.remove('notify-me__submit--success');
      submitButton.disabled = false;
      submitButton.setAttribute('aria-busy', 'false');
      const submitText = submitButton.querySelector(".notify-me__submit-text");
      if (submitText instanceof HTMLElement) {
        submitText.textContent = "Notify Me";
      }
    }
  }

  /**
   * Handles API errors
   * @param {NotifyMeApiResponse} data
   */
  #handleApiError(data) {
    /** @type {string} */
    let message;

    switch (data.code) {
      case StatusCodes.BAD_REQUEST:
        if (data.error === ErrorCodes.INVALID_CUSTOMER_ID) {
          message = this.#getTranslation('notify_me.error_invalid_customer', 'Customer not found. Please try again.');
        } else if (data.error === ErrorCodes.NOTIFICATION_EXISTS) {
          message = this.#getTranslation('notify_me.already_subscribed', 'You have already subscribed for Notify Me Service! You will be notified when the item is back in stock.');
        } else {
          message = data.message || this.#getTranslation('notify_me.error_generic', 'Something went wrong. Please try again.');
        }
        break;

      case StatusCodes.UNAUTHORIZED:
        message = this.#getTranslation('notify_me.error_unauthorized', 'Session expired. Please refresh the page and try again.');
        break;

      case StatusCodes.VALIDATION_ERROR:
        message = this.#formatValidationErrors(data.error);
        break;

      case StatusCodes.SERVER_ERROR:
      default:
        message = this.#getTranslation('notify_me.error_server', 'Server error. Please try again later.');
        break;
    }

    this.#showMessage(message, 'error');
  }

  /**
   * Formats validation errors from API
   * @param {Object|string|undefined} errors
   * @returns {string}
   */
  #formatValidationErrors(errors) {
    if (typeof errors === 'string') {
      return errors;
    }

    if (errors && typeof errors === 'object') {
      /** @type {string[]} */
      const errorMessages = [];

      /** @type {Record<string, string>} */
      const fieldMapping = {
        sku: 'sku',
        email: 'email',
        customerFirstName: 'name',
      };

      for (const [field, messages] of Object.entries(errors)) {
        if (Array.isArray(messages)) {
          errorMessages.push(...messages);
        } else {
          errorMessages.push(String(messages));
        }

        // Highlight the invalid field using the new error methods
        const mappedField = fieldMapping[field];
        if (mappedField) {
          const inputField = /** @type {HTMLInputElement | null} */ (this.querySelector(`input[name="${mappedField}"]`));
          if (inputField) {
            const errorMessage = Array.isArray(messages) ? messages[0] : String(messages);
            this.#setFieldError(inputField, errorMessage);
          }
        }
      }

      return errorMessages.join('. ');
    }

    return this.#getTranslation('notify_me.error_validation', 'Please check your input and try again.');
  }

  /**
   * Handles fetch errors
   * @param {Error} error
   */
  #handleError(error) {
    if (error.name === 'AbortError') {
      // Request was cancelled, ignore
      return;
    }

    console.error('Notify Me submission error:', error);

    const message = this.#getTranslation('notify_me.error_network', 'Network error. Please check your connection and try again.');

    this.#showMessage(message, 'error');
  }

  /**
   * Sets the loading state of the form
   * @param {boolean} isLoading
   */
  #setLoadingState(isLoading) {
    this.#isSubmitting = isLoading;

    const submitButton = /** @type {HTMLButtonElement | null} */ (this.submitButton);
    if (submitButton) {
      // submitButton.disabled = isLoading;
      submitButton.setAttribute('aria-busy', String(isLoading));

      if (isLoading) {
        submitButton.classList.add('notify-me__submit--loading');
      } else {
        submitButton.classList.remove('notify-me__submit--loading');
      }
    }

    // Disable all inputs during submission
    const inputs = this.querySelectorAll('input');
    inputs.forEach((input) => {
      const inputElement = /** @type {HTMLInputElement} */ (input);
      if (inputElement.type !== 'hidden') {
        inputElement.disabled = isLoading;
      }
    });
  }

  /**
   * Shows a message to the user
   * @param {string} message
   * @param {'success' | 'error'} type
   */
  #showMessage(message, type) {
    // Remove any existing message
    const existingMessage = this.querySelector('.notify-me__message');
    existingMessage?.remove();

    // hide the form states
    if (type === 'success') {
      this.#hideFormStates();
    }

    // Create message element
    const messageElement = document.createElement('div');
    messageElement.className = `notify-me__message notify-me__message--${type}`;
    messageElement.setAttribute('role', type === 'error' ? 'alert' : 'status');
    messageElement.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
    messageElement.textContent = message;

    // Insert message before the submit button
    const submitButton = this.querySelector('.notify-me__form-footer');
    if (submitButton) {
      submitButton.parentNode?.insertBefore(messageElement, submitButton);
    } else {
      this.appendChild(messageElement);
    }
  }

  /**
   * Hides the form states
   */
  #hideFormStates() {
    const hiddenElements = this?.closest('notify-me-component')?.querySelectorAll('.notify-me__dialog-content , .notify-me__field, .notify-me__dialog-heading');

    if (hiddenElements) {
      hiddenElements.forEach((element) => {
        element.classList.add('hidden');
      });
    }
  }

  /**
   * Shows the form states
   */
  #showFormStates() {
    this.querySelector('.notify-me__message')?.remove();

    const hiddenElements = this?.closest('notify-me-component')?.querySelectorAll('.notify-me__dialog-content , .notify-me__field, .notify-me__dialog-heading');
    if (hiddenElements) {
      hiddenElements.forEach((element) => {
        element.classList.remove('hidden');
      });
    }
  }

  /**
   * Gets a translation string with fallback
   * @param {string} key
   * @param {string} fallback
   * @returns {string}
   */
  #getTranslation(key, fallback) {
    // Remove 'notify_me.' prefix if present for consistency
    const cleanKey = key.replace(/^notify_me\./, '');
    return getNotifyMeTranslation(this, cleanKey, fallback);
  }

  /**
   * Dispatches a notify me added event
   * @param {NotifyMeApiResponse} data
   */
  #dispatchNotifyMeAddedEvent(data) {
    this.dispatchEvent(new NotifyMeAddedEvent(data));
  }
}

// Register custom elements
if (!customElements.get('notify-me-form-component')) {
  customElements.define('notify-me-form-component', NotifyMeFormComponent);
}

export { NotifyMeFormComponent };